/**
 * +prof: pure text / colour models for the gathering HUD (tooltip, Professions panel, skill-up toast,
 * reward pop labels). DOM-free and unit-tested.
 */
import type { GatherNodeDef, GatherProfession } from '@keystone/sim';
import { GATHER_COLOR_CSS, GATHER_NODE_KINDS, GATHER_TIERS, PROF_SKILL_CAP, PROFESSION_NAME, gatherColor, gatherNodeKind, type GatherColor } from '@keystone/content';
import type { GatherLine } from '@keystone/progression';

export const COLOR_HINT: Readonly<Record<GatherColor, string>> = {
  red: 'Too difficult',
  orange: 'Skill-up guaranteed',
  yellow: 'Skill-up likely',
  green: 'Skill-up unlikely',
  grey: 'No skill-ups',
};

const RES_NAME: Readonly<Record<string, string>> = { gold: 'Gold', fangsteel: 'Fangsteel', venom: 'Viper Venom', moltskin: 'Moltskin', scale: 'Coil Scale' };

/** "Ashen Coil Scale" / "Fangsteel". */
export function resourceName(resource: string, grade?: string): string {
  if (resource === 'scale') return `${grade ? grade[0]!.toUpperCase() + grade.slice(1) + ' ' : ''}Coil Scale`;
  return RES_NAME[resource] ?? resource;
}

export interface NodeTooltip {
  title: string;
  /** "Foraging · Tier 2". */
  sub: string;
  /** "Requires Foraging 35" (red when the skill is short) or the skill-up colour line. */
  req: { text: string; color: string };
  flavor: string;
  /** What it can yield ("Fangsteel", "Coil Scale (Ashen)", "Viper Venom (rare)"). */
  yields: string[];
  /** "Depleted: back in 1:23" / "Walk closer to gather" / "Press T or right-click to gather". */
  state: { text: string; color: string };
  color: GatherColor;
}

const mmss = (ms: number): string => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export function nodeTooltip(node: Pick<GatherNodeDef, 'kind' | 'profession' | 'tier' | 'req'>, skill: number, backInMs: number, dist: number, reach = 3.5): NodeTooltip {
  const kind = gatherNodeKind(node.kind);
  const prof = PROFESSION_NAME[node.profession];
  const color = gatherColor(skill, node.req);
  const req =
    color === 'red'
      ? { text: `Requires ${prof} ${node.req}`, color: GATHER_COLOR_CSS.red }
      : { text: `${prof} ${skill} · ${COLOR_HINT[color]}`, color: GATHER_COLOR_CSS[color] };
  const yields = (kind?.yields ?? []).filter((y) => y.resource !== 'gold').map((y) => `${resourceName(y.resource, y.grade)}${y.rare || y.chance < 0.05 ? ' (rare)' : ''}`);
  const state =
    backInMs > 0
      ? { text: `Depleted: back in ${mmss(backInMs)}`, color: '#9aa3b5' }
      : color === 'red'
        ? { text: `Raise your ${prof} skill on easier nodes`, color: '#ff7a7a' }
        : dist > reach
          ? { text: 'Walk closer to gather', color: '#cfd6e2' }
          : { text: 'Press T or right-click to gather', color: '#8ef08e' };
  return { title: kind?.name ?? node.kind, sub: `${prof} · Tier ${node.tier}`, req, flavor: kind?.flavor ?? '', yields, state, color };
}

export interface ProfRow {
  prof: GatherProfession;
  name: string;
  skill: number;
  cap: number;
  pct: number;
  /** "Next: Bloomcap at 35" / "All Mossvale nodes unlocked". */
  next: string;
  tiers: { tier: number; name: string; req: number; color: GatherColor; css: string; gathered: number }[];
}

export function profRows(skills: Record<GatherProfession, number>, byKind: Record<string, number> = {}): ProfRow[] {
  return (['foraging', 'mining'] as const).map((prof) => {
    const skill = skills[prof] ?? 1;
    const tiers = GATHER_TIERS.map((t) => {
      const k = GATHER_NODE_KINDS.find((x) => x.profession === prof && x.tier === t.tier)!;
      const color = gatherColor(skill, t.req);
      return { tier: t.tier, name: k.name, req: t.req, color, css: GATHER_COLOR_CSS[color], gathered: byKind[k.id] ?? 0 };
    });
    const nxt = tiers.find((t) => skill < t.req);
    return {
      prof,
      name: PROFESSION_NAME[prof],
      skill,
      cap: PROF_SKILL_CAP,
      pct: Math.max(0, Math.min(1, skill / PROF_SKILL_CAP)),
      next: nxt ? `Next: ${nxt.name} at ${nxt.req}` : 'Every Mossvale node unlocked',
      tiers,
    };
  });
}

/** "Foraging 23 → 26". */
export function skillUpText(prof: GatherProfession, before: number, after: number): string {
  return `${PROFESSION_NAME[prof]} ${before} → ${after}`;
}

/** A gather line as a reward pop label ("Fangsteel", "Ashen Coil Scale ×2"). */
export function lineLabel(l: Pick<GatherLine, 'resource' | 'amount' | 'grade'>): string {
  const n = resourceName(l.resource, l.grade);
  return l.amount > 1 ? `${n} ×${l.amount}` : n;
}

/** Pop rarity per resource (matches the world-loot glow colours). */
export function lineRarity(l: Pick<GatherLine, 'resource' | 'grade' | 'rare'>): 'common' | 'uncommon' | 'rare' | 'epic' {
  if (l.resource === 'scale') return l.grade && l.grade !== 'ashen' ? 'epic' : 'uncommon';
  if (l.resource === 'venom' || l.resource === 'moltskin') return 'rare';
  return 'common';
}
